// Record by hand (#152 B1): the form's rules and the body POST /cofas takes. Pure, so it is tested.
//
// A person reads a scan the intake job could not, and types what it prints. Everything typed is
// MANUAL_READ (rule 19): it never gets a machine PASS, it is judged against the material's spec
// (rule 21) and a person decides (rule 18). So the form asks for what the certificate PRINTS - the
// lot as printed (never D365's batch: rule 19's "tidied" lot), each result as printed - and adds
// nothing it does not show.
import type { SignatureKind } from './goodsInModel';

export interface LotRow {
  lot: string;
  manufacturedDate: string;
  bestBeforeDate: string;
}

export interface ResultRow {
  /** Our spec check this row was filled from, or null for one typed in. */
  specParameterId: number | null;
  parameter: string;
  result: string;
  unit: string;
  printedLimit: string;
}

export interface ByHandForm {
  receiptId: number | null;
  lots: LotRow[];
  results: ResultRow[];
  issuer: string;
  product: string;
  signatureKind: SignatureKind | '';
  signatureName: string;
  signatureRole: string;
  issuedDate: string;
  /** Read with AI (#152 step 4): what the model read, by field key ('lot:0', 'result:Moisture', 'issuer'...).
   *  A value still equal to it, and not ticked, is unchecked: Save waits for every one. */
  ai: Record<string, string>;
  confirmed: Record<string, true>;
}

/** A spec check as GET /specs returns it (quality_spec). */
export interface SpecParameter {
  id: number;
  code: string;
  name: string;
  unit: string | null;
  checkKind: string;
}

export const emptyLot = (): LotRow => ({ lot: '', manufacturedDate: '', bestBeforeDate: '' });
export const emptyResult = (): ResultRow => ({ specParameterId: null, parameter: '', result: '', unit: '', printedLimit: '' });

export function emptyForm(receiptId: number | null): ByHandForm {
  return { receiptId, lots: [emptyLot()], results: [], issuer: '', product: '', signatureKind: '', signatureName: '', signatureRole: '', issuedDate: '', ai: {}, confirmed: {} };
}

/** One row per COFA check of the material's spec, in its order, the name and unit pre-set: the person
 *  types what the certificate prints beside each, and leaves blank what it does not report. */
export function resultsFromSpec(params: readonly SpecParameter[]): ResultRow[] {
  return params
    .filter((p) => p.checkKind === 'COFA')
    .map((p) => ({ specParameterId: p.id, parameter: p.name, result: '', unit: p.unit ?? '', printedLimit: '' }));
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** The PO the email names, offered for the person to confirm (UI/UX audit, 31 Oct 2026, UX-48). An
 *  unmatched email such as "COA_PO025517_..." left the PO box empty, though the subject said which PO
 *  it was. Read from the subject, then the attachment's name: our PO, `PO` and six digits, not inside a
 *  longer word or number. Null when the intake job already named a PO (the box is filled from it), when
 *  nothing names one, or when they name more than one - two candidates are a person's call, never a
 *  guess. A SUGGESTION only: the screen offers it as a button and fills nothing until it is pressed, and
 *  the line is still chosen by hand. */
export function suggestedPo(row: { po: string | null; subject: string | null; attachmentName: string }): string | null {
  if (row.po) return null;
  const found = new Set<string>();
  for (const text of [row.subject ?? '', row.attachmentName]) {
    for (const m of text.matchAll(/(?<![A-Za-z0-9])PO[\s_-]?(\d{6})(?!\d)/gi)) found.add(`PO${m[1]}`);
  }
  return found.size === 1 ? [...found][0] : null;
}

/** Everything that stops Save, each as a sentence. Empty = it can be saved. */
export function problems(f: ByHandForm): string[] {
  const out: string[] = [];
  if (f.receiptId === null) out.push('Choose the PO line the certificate is for.');
  const lots = f.lots.filter((l) => l.lot.trim() !== '' || l.manufacturedDate || l.bestBeforeDate);
  if (lots.length === 0) out.push('Type at least one lot, exactly as the certificate prints it.');
  lots.forEach((l, i) => {
    if (!l.lot.trim()) out.push(`Lot ${i + 1} has dates but no lot.`);
    for (const [d, what] of [
      [l.manufacturedDate, 'production'],
      [l.bestBeforeDate, 'best-before'],
    ] as const) {
      if (d && !DAY.test(d)) out.push(`Lot ${i + 1}: the ${what} date is not a date.`);
    }
    if (l.manufacturedDate && l.bestBeforeDate && l.manufacturedDate > l.bestBeforeDate) out.push(`Lot ${i + 1}: produced after its best-before date.`);
  });
  const seen = new Set<string>();
  for (const l of lots) {
    const k = l.lot.trim().toUpperCase();
    if (k && seen.has(k)) out.push(`Lot ${l.lot.trim()} is typed twice: type each lot once.`);
    seen.add(k);
  }
  f.results.forEach((r, i) => {
    if ((r.result.trim() || r.printedLimit.trim()) && !r.parameter.trim()) out.push(`Result ${i + 1} has no parameter name.`);
    if (r.specParameterId === null && r.parameter.trim() && !r.result.trim()) out.push(`${r.parameter.trim()}: type the result as printed, or remove the row.`);
  });
  if (!f.signatureKind) out.push('Say how the certificate is signed, or that nothing authorises it.');
  if (f.signatureKind === 'PRINTED_NAME' && !f.signatureName.trim()) out.push('A printed name needs the name as printed.');
  if (f.issuedDate && !DAY.test(f.issuedDate)) out.push('The issue date is not a date.');
  const unchecked = uncheckedAi(f).length;
  if (unchecked) out.push(`${unchecked} value${unchecked === 1 ? '' : 's'} read by AI still to check against the scan (tick each, or correct it).`);
  return out;
}

/* ── Read with AI ─────────────────────────────────────────── */

/** What POST /cofa-intake/{id}/prefill returns as `suggestion`. */
export interface Suggestion {
  isCertificate: boolean;
  notCertificateReason: string | null;
  lots: Array<{ lot: string; manufacturedDate: string | null; bestBeforeDate: string | null }>;
  results: Array<{ parameter: string; result: string; unit: string | null; printedLimit: string | null }>;
  issuer: string | null;
  product: string | null;
  signature: { kind: SignatureKind; name: string | null; role: string | null };
  issuedDate: string | null;
  poAsPrinted: string | null;
}

const norm = (x: string) => x.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/** The current value of a field key. */
export function valueOf(f: ByHandForm, key: string): string {
  const [kind, rest = ''] = key.split(':');
  if (kind === 'lot') {
    const [i, part] = rest.split('.');
    const l = f.lots[Number(i)];
    return !l ? '' : part === 'made' ? l.manufacturedDate : part === 'bb' ? l.bestBeforeDate : l.lot;
  }
  if (kind === 'result') return f.results.find((r) => r.parameter === rest)?.result ?? '';
  if (key === 'issuer') return f.issuer;
  if (key === 'product') return f.product;
  if (key === 'signature.kind') return f.signatureKind;
  if (key === 'signature.name') return f.signatureName;
  if (key === 'signature.role') return f.signatureRole;
  if (key === 'issued') return f.issuedDate;
  return '';
}

/** The AI's values not yet checked: still exactly what it read, and not ticked. */
export function uncheckedAi(f: ByHandForm): string[] {
  return Object.keys(f.ai).filter((k) => f.ai[k] !== '' && !f.confirmed[k] && valueOf(f, k) === f.ai[k]);
}

/** Whether one field still waits for a person. */
export const isUnchecked = (f: ByHandForm, key: string) => uncheckedAi(f).includes(key);

/** The form filled with what the model read. A result goes on its spec row when the names agree,
 *  else on a row of its own; nothing already typed is overwritten. */
export function applySuggestion(f: ByHandForm, s: Suggestion): ByHandForm {
  const ai: Record<string, string> = {};
  const typedLots = f.lots.filter((l) => l.lot.trim());
  const lots = typedLots.length ? f.lots : s.lots.length ? s.lots.map((l) => ({ lot: l.lot, manufacturedDate: l.manufacturedDate ?? '', bestBeforeDate: l.bestBeforeDate ?? '' })) : f.lots;
  if (!typedLots.length) {
    s.lots.forEach((l, i) => {
      ai[`lot:${i}`] = l.lot;
      if (l.manufacturedDate) ai[`lot:${i}.made`] = l.manufacturedDate;
      if (l.bestBeforeDate) ai[`lot:${i}.bb`] = l.bestBeforeDate;
    });
  }
  const results = f.results.map((r) => ({ ...r }));
  for (const r of s.results) {
    const row = results.find((x) => norm(x.parameter) === norm(r.parameter));
    if (row && row.result.trim()) continue;
    if (row) {
      row.result = r.result;
      if (r.unit && !row.unit) row.unit = r.unit;
      if (r.printedLimit) row.printedLimit = r.printedLimit;
      ai[`result:${row.parameter}`] = r.result;
    } else {
      results.push({ specParameterId: null, parameter: r.parameter, result: r.result, unit: r.unit ?? '', printedLimit: r.printedLimit ?? '' });
      ai[`result:${r.parameter}`] = r.result;
    }
  }
  const take = (current: string, read: string | null, key: string) => {
    if (current.trim() || !read) return current;
    ai[key] = read;
    return read;
  };
  const next: ByHandForm = {
    ...f,
    lots,
    results,
    issuer: take(f.issuer, s.issuer, 'issuer'),
    product: take(f.product, s.product, 'product'),
    signatureKind: f.signatureKind || s.signature.kind,
    signatureName: take(f.signatureName, s.signature.name, 'signature.name'),
    signatureRole: take(f.signatureRole, s.signature.role, 'signature.role'),
    issuedDate: take(f.issuedDate, s.issuedDate && DAY.test(s.issuedDate) ? s.issuedDate : null, 'issued'),
    ai: { ...f.ai, ...ai },
    confirmed: f.confirmed,
  };
  if (!f.signatureKind) next.ai['signature.kind'] = s.signature.kind;
  return next;
}

/** The number a result reads as when it is plainly one; anything else goes as printed, for the server to read. */
function plainNumber(s: string): number | null {
  const t = s.trim();
  return /^-?\d+(\.\d{1,4})?$/.test(t) ? Number(t) : null;
}

/** The body POST /cofas takes. Rows left blank are not sent: a blank is "not reported". */
export function toBody(f: ByHandForm, intakeId: number) {
  const opt = (s: string) => (s.trim() ? s.trim() : undefined);
  return {
    intakeId,
    receiptId: f.receiptId,
    lots: f.lots
      .filter((l) => l.lot.trim())
      .map((l) => ({ lot: l.lot.trim(), manufacturedDate: opt(l.manufacturedDate), bestBeforeDate: opt(l.bestBeforeDate) })),
    results: f.results
      .filter((r) => r.parameter.trim() && r.result.trim())
      .map((r) => ({ parameter: r.parameter.trim(), result: r.result.trim(), value: plainNumber(r.result), unit: opt(r.unit), printedLimit: opt(r.printedLimit) })),
    issuer: opt(f.issuer),
    product: opt(f.product),
    signature: !f.signatureKind
      ? undefined
      : f.signatureKind === 'NONE' || f.signatureKind === 'IMAGE'
        ? { kind: f.signatureKind }
        : { kind: f.signatureKind, name: opt(f.signatureName), role: opt(f.signatureRole) },
    issuedDate: opt(f.issuedDate),
    // The measure: per AI value, what the person saved (#152 step 4). Absent when AI was not used.
    prefill: Object.keys(f.ai).length ? { checks: Object.keys(f.ai).map((k) => ({ field: k, ai: f.ai[k], final: valueOf(f, k) })) } : undefined,
  };
}
